// Global search palette. Opens with Cmd/Ctrl-K from anywhere in
// the app (Req 13.1). Keyboard-first: arrow keys navigate, Enter
// jumps, Escape closes.
//
// Search is intentionally limited to server-visible messages. DMs,
// secrets, and other end-to-end encrypted content never enter the
// server-side search index.

import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  Filter,
  LoaderCircle,
  Search,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  Dialog,
  DialogContent,
  cn,
} from "@link/ui";

import { api } from "../lib/api";
import { readSessionToken } from "../lib/http";
import { renderForSurface } from "../lib/secrets-placeholder";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (spaceXid: string, channelXid: string) => void;
}

export interface SearchItem {
  id: string;
  spaceXid: string;
  spaceName: string;
  channelXid: string;
  channelName: string;
  channelType: "text" | "snippet" | "file" | "secret";
  authorName: string;
  authorTag: string;
  timestamp: string;
  body: string;
}

type ChannelTypeFilter = "all" | "text" | "snippet" | "file";

export function SearchDialog({ open, onOpenChange, onNavigate }: Props) {
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<ChannelTypeFilter>("all");
  const [active, setActive] = useState(0);
  const [results, setResults] = useState<SearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-focus the input when the dialog opens.
  useEffect(() => {
    if (open) {
      setActive(0);
      // Defer to let Radix mount.
      window.setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQuery("");
      setTypeFilter("all");
      setResults([]);
      setSearching(false);
      setSearchError(null);
    }
  }, [open]);

  // Debounced live fetch. No synthetic fallback is used: an empty
  // index, a signed-out session, or an unavailable search service is
  // shown honestly instead of displaying demo conversations.
  useEffect(() => {
    if (!open) return undefined;
    if (readSessionToken() === null) {
      setResults([]);
      setSearching(false);
      setSearchError("Sign in to search server messages.");
      return undefined;
    }
    const q = query.trim();
    if (q.length === 0) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return undefined;
    }
    setSearching(true);
    setSearchError(null);
    let cancelled = false;
    const handle = window.setTimeout(() => {
      api.search
        .query({
          q,
          channelType:
            typeFilter === "text" ||
            typeFilter === "snippet" ||
            typeFilter === "file"
              ? typeFilter === "snippet"
                ? "snippets"
                : typeFilter === "file"
                  ? "files"
                  : "text"
              : undefined,
        })
        .then((res) => {
          if (cancelled) return;
          setResults(
            res.results.map((r) => ({
              id: r.message_xid,
              spaceXid: r.space_xid,
              spaceName: r.space_name,
              channelXid: r.channel_xid,
              channelName: r.channel_name,
              channelType: r.channel_type,
              authorName: r.author_display_name,
              authorTag: r.author_account_xid.slice(-4),
              timestamp: new Date(r.created_at).toLocaleString(),
              body: r.snippet,
            })),
          );
          setSearching(false);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setSearching(false);
          setSearchError("Search is temporarily unavailable.");
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, typeFilter, open]);

  const onKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const result = results[active];
      if (result === undefined) return;
      onNavigate(result.spaceXid, result.channelXid);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[min(720px,95vw)] gap-0 overflow-hidden border-cloud-200 bg-white p-0 text-cloud-700 shadow-2xl"
        onKeyDown={onKey}
      >
        <div className="flex items-center gap-2 border-b border-cloud-200 px-4 py-3">
          <Search className="h-4 w-4 text-cloud-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search server messages…"
            className="h-9 flex-1 bg-transparent text-sm outline-none placeholder:text-cloud-400"
          />
        </div>

        {/* Filter strip */}
        <div className="flex flex-wrap items-center gap-1 border-b border-cloud-200 bg-cloud-50/60 px-3 py-2 text-xs">
          <Filter className="h-3 w-3 text-cloud-400" />
          {(
            [
              ["all", "All"],
              ["text", "Text"],
              ["snippet", "Snippets"],
              ["file", "Files"],
            ] satisfies ReadonlyArray<[ChannelTypeFilter, string]>
          ).map(([id, label]) => (
            <FilterChip
              key={id}
              active={typeFilter === id}
              onClick={() => {
                setTypeFilter(id);
                setActive(0);
              }}
            >
              {label}
            </FilterChip>
          ))}
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {searching ? (
            <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-cloud-500">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-cloud-500">
              {searchError ??
                (query.trim().length === 0
                  ? "Type to search messages in your servers."
                  : `No results for "${query}".`)}
            </div>
          ) : (
            <ul>
              {results.map((it, i) => (
                <li key={it.id}>
                  <ResultRow
                    item={it}
                    query={query}
                    active={i === active}
                    onHover={() => setActive(i)}
                    onClick={() => {
                      onNavigate(it.spaceXid, it.channelXid);
                      onOpenChange(false);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-3 border-t border-cloud-200 bg-cloud-50/60 px-4 py-2 text-[10px] text-cloud-500">
          <span className="inline-flex items-center gap-1">
            <Hint>
              <ArrowUp className="h-2.5 w-2.5" />
              <ArrowDown className="h-2.5 w-2.5" />
            </Hint>
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <Hint>
              <CornerDownLeft className="h-2.5 w-2.5" />
            </Hint>
            open
          </span>
          <span className="inline-flex items-center gap-1">
            <Hint>esc</Hint>
            close
          </span>
          <span className="ml-auto">
            {results.length} result{results.length === 1 ? "" : "s"}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors",
        active
          ? "bg-strawberry-gradient text-white shadow-sm"
          : "bg-white text-cloud-600 ring-1 ring-cloud-200 hover:bg-cloud-50",
      )}
    >
      {children}
    </button>
  );
}

function ResultRow({
  item,
  query,
  active,
  onHover,
  onClick,
}: {
  item: SearchItem;
  query: string;
  active: boolean;
  onHover: () => void;
  onClick: () => void;
}) {
  const decision = renderForSurface({
    kind: item.channelType,
    surface: "search",
    body: item.body,
  });

  return (
    <button
      type="button"
      onMouseEnter={onHover}
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors",
        active ? "bg-strawberry-50" : "hover:bg-cloud-50",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[11px] text-cloud-500">
          <span className="font-semibold text-cloud-700">
            {item.spaceName}
          </span>
          <span className="text-cloud-300">›</span>
          <span>#{item.channelName}</span>
          <span className="text-cloud-300">·</span>
          <span>
            {item.authorName}
            <span className="text-cloud-400"> · @{item.authorName.toLowerCase()}</span>
          </span>
          <span className="ml-auto text-cloud-400">{item.timestamp}</span>
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-cloud-800">
          {decision.redacted ? (
            <span className="italic text-cloud-500">{decision.display}</span>
          ) : (
            <Highlight text={decision.display} query={query} />
          )}
        </p>
      </div>
    </button>
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (q.length === 0) return <>{text}</>;
  const re = new RegExp(`(${escapeRegex(q)})`, "gi");
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) =>
        re.test(p) ? (
          <mark
            key={i}
            className="rounded bg-strawberry-100 px-0.5 text-strawberry-800"
          >
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Hint({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex items-center gap-0.5 rounded border border-cloud-300 bg-white px-1 py-0.5 text-[10px] font-semibold text-cloud-700">
      {children}
    </kbd>
  );
}
